import { Instance, types } from 'mobx-state-tree'
import { Jaw } from '../shared/Constants'
import { Recommendation, RecommendationInstance } from './Recommendation'
import { Requirement, RequirementInstance } from './Requirement'

export const Offer = types
  .model('Offer', {
    code: types.optional(types.string, ''),
    upperJawRequirement: types.maybeNull(Requirement),
    lowerJawRequirement: types.maybeNull(Requirement),
    upperJawRecommendations: types.array(Recommendation),
    lowerJawRecommendations: types.array(Recommendation),
    insuranceType: types.optional(types.enumeration(['private', 'public']), 'public'),
    insuranceBonus: types.optional(
      types.enumeration(['zero_to_four_years', 'five_to_nine_years', 'at_least_ten_years', 'free', 'unknown']),
      'unknown'
    ),
    zipCode: types.optional(types.string, ''),
    email: types.optional(types.string, ''),
    costs: types.optional(types.number, 0),
    allowance: types.optional(types.number, 0),
  })
  .actions((self) => ({
    setProp: (prop: string, value: any) => {
      self[prop] = value
    },
    setRecommendationsForJaw: (jaw: Jaw, recommendations: RecommendationInstance[]) => {
      self[`${jaw}JawRecommendations`] = recommendations
    },
    setInsuranceType: (insuranceType: 'private' | 'public') => {
      self.insuranceType = insuranceType
    },
    setInsuranceBonus: (
      insuranceBonus: 'zero_to_four_years' | 'five_to_nine_years' | 'at_least_ten_years' | 'free' | 'unknown'
    ) => {
      self.insuranceBonus = insuranceBonus
    },
    makeJawRequirement: (jaw: Jaw) => {
      self[`${jaw}JawRequirement`] = Requirement.create({
        missingTeeth: [],
        jaw,
        material: 'premium',
        boneGrafting: 'no',
        boneGraftingType: 'normal',
      })
    },
  }))
  .views((self) => {
    const views = {
      hasJawRequirement: (jaw: Jaw) => {
        return !!self[`${jaw}JawRequirement`]
      },
      getJawRequirement: (jaw: Jaw): RequirementInstance => {
        return self[`${jaw}JawRequirement`]
      },
      getJawRecommendations: (jaw: Jaw): RecommendationInstance[] => {
        return self[`${jaw}JawRecommendations`]
      },
      get selfExpense() {
        return self.costs - self.allowance
      },
      get recommendationSums() {
        const sums = {}
        ;[...self.upperJawRecommendations, ...self.lowerJawRecommendations].forEach((recommendation) => {
          const key = recommendation.attachmentType || recommendation.recommendationType
          sums[key] = (sums[key] || 0) + recommendation.count
        })
        return sums
      },
      get stringifiedRecommendations() {
        const translations = {
          crown: ['Implantat', 'Implantate'],
          bridge: ['Brückenglied', 'Brückenglieder'],
          locator: ['Locator', 'Locatoren'],
          telescope: ['Teleskop', 'Teleskope'],
        }

        const sums = views.recommendationSums

        const parts = []
        for (let type in sums) {
          const [singular, plural] = translations[type]
          const count = sums[type]
          parts.push(`${count} ${count === 1 ? singular : plural}`)
        }
        return parts.join(', ')
      },
    }
    return views
  })

export type OfferInstance = Instance<typeof Offer>
